import Breadcrumb from "@components/atoms/Breadcrumb";
import { useRouter } from "next/router";
import ProductInfo from "@components/features/product-detail/info/ProductInfo";
import ProductSpecTable from "@components/features/product-detail/specification/ProductSpecTable";
import ProductSpecSheet from "@components/features/product-detail/specification/ProductSpecSheet";
import ProductCustomerReview from "@components/features/product-detail/specification/ProductCustomerReview";
import RelatedProducts from "@components/features/product-detail/related-product/RelatedProducts";
import { MockupProductReviews } from "@mockup/productReviews.mockup";
import { NextPage } from "next";
import Loader from "@components/atoms/Loader";
import {
  useProductDetail,
  useProductKeyExchange,
  useUpsellsProducts,
} from "@api/products/products";
import { useReviewProduct, useRatingSummary } from "@api/products/reviews";
import {
  RELATED_PAGE_SIZE,
  REVIEW_DEFAULT_PAGE,
  REVIEW_PAGE_SIZE,
  UPSELL_PAGE_SIZE,
} from "@const/products.const";
import UpsellsProducts from "@components/features/product-detail/detail/UpsellsProducts";
import { BreadcrumbItem } from "@interfaces/components/breadcrumb.interface";
import { RECOMMEND_CATEGORY_ID as ROOT_CATEGORY_ID } from "@const/recommend-category.const";
import { useState, useEffect } from "react";
// SEO
import {
  convertKeywordstoArray,
  convertRobotstoBoolean,
} from "@utils/seo.util";
import { NextSeo } from "next-seo";
import { SEO_SITEURL } from "@const/seo.const";
import TagManager, { DataLayerArgs } from "react-gtm-module"; // DataLayer GTM
import { isWebview, hideMenuBarNative } from "@utils/webview.util";
// SSR
import { GetStaticProps } from "next";
import { serverSideAdminToken } from "@api/server-side/token";
import { serverSideUrlKeyExchange } from "@api/server-side/cms-page";
import {
  serverSideMetadataProductDetail,
  serverSideProductDetail,
  serverSideRelatedProducts,
  serverSideUpSellProducts,
} from "@api/server-side/product-page";
import { SWRConfig } from "swr";
import type { SWRConfiguration } from "swr";
import { randomPageInRange } from "@utils/common.util";
import { useSelector } from "react-redux";
import {
  isAuthenticatedSelector,
  userSelector,
} from "@stores/slices/userSlice";
import { setRecentViewIdStorage } from "@stores/storages/recent-view.storage";
import { customerViewProduct } from "@api/products/recentviews";
import RecentView from "@components/features/product-detail/recent-view/RecentView";
import SkeletonProductCart from "@components/atoms/skeleton/ui-product-cart";
import SkeletonProductInfo from "@components/atoms/skeleton/ui-product-info";

const ProductDetailPage: NextPage = ({ fallback }: SWRConfiguration) => {
  const router = useRouter();
  const { pkey: productKey } = router.query;

  const {
    data: dataProductKeyExchange,
    isLoading: isLoadingProductKeyExchange,
  } = useProductKeyExchange(productKey as string);

  if (!isLoadingProductKeyExchange && dataProductKeyExchange === undefined)
    router.push("/404");

  const {
    data: dataProductDetail,
    isLoading: isLoadingProductDetail,
    error: errorProductDetail,
  } = useProductDetail(dataProductKeyExchange?.id);

  if (dataProductDetail === undefined && errorProductDetail) router.push("/");

  // get useReviewProduct
  const productID = dataProductKeyExchange?.id;
  const [filteredReviewsRate, setFilteredReviewsRate] = useState<
    string | undefined
  >(undefined);
  const [filteredReviewsImg, setFilteredReviewsImg] = useState<
    string | undefined
  >(undefined);
  const [reviewCurrentPage, setReviewCurrentPage] =
    useState<number>(REVIEW_DEFAULT_PAGE);
  const { data: dataReviewProducts, isLoading: isLoadingReviewProducts } =
    useReviewProduct({
      productId: productID,
      productDisplayConfig: {
        currentPage: reviewCurrentPage,
        pageSize: REVIEW_PAGE_SIZE,
      },
      ratingStar: filteredReviewsRate,
      filterImage: filteredReviewsImg,
    });

  const { data: dataReviewSummary, isLoading: isLoadingReviewSummary } =
    useRatingSummary({
      productId: productID,
    });

  const totalReviews = dataReviewSummary?.[0]?.ratings
    ? dataReviewSummary[0].ratings.reduce(
        (acc, rating) => acc + rating.total_count,
        0
      )
    : 0;

  const totalReviewsFilter = dataReviewProducts?.total_count || 0;
  const totalImageCount = dataReviewSummary?.[0]?.images?.[0]?.total_count || 0;

  useEffect(() => {
    setReviewCurrentPage(REVIEW_DEFAULT_PAGE);
  }, [productID]);

  // making breadcrum arrays
  const breadcrums: BreadcrumbItem[] = [
    {
      id: 1,
      name: "หน้าแรก",
      link: "/",
    },
    {
      id: 2,
      name: fallback?.details.name as string,
      link: undefined,
    },
  ];

  // SSR FOR SEO
  const { noIndex, noFollow } = convertRobotstoBoolean(
    fallback?.metatags.robots as string
  );
  const keywords = convertKeywordstoArray(
    fallback?.metatags.keywords as string
  );

  // Datalayer GTM
  useEffect(() => {
    if (dataProductDetail) {
      const variables: Record<string, any> = {
        event: "topvalue.next",
        PageType: "Productpage",
        ProductName: dataProductDetail?.name,
        ProductID: dataProductDetail?.sku,
        ProductCategory: dataProductDetail.category,
        price:
          dataProductDetail?.special_price > 0
            ? dataProductDetail?.special_price
            : dataProductDetail?.price,
        Promotion:
          dataProductDetail.promotion !== "" ? dataProductDetail.promotion : "",
      };
      const dataLayerArgs: DataLayerArgs = {
        dataLayer: { variables },
      };
      TagManager.dataLayer(dataLayerArgs);
    }
  }, [dataProductDetail, router.pathname]);

  useEffect(() => {
    if (isWebview()) {
      hideMenuBarNative();
    }
  }, []);

  // Recent View Product
  const user = useSelector(userSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  if (isAuthenticated) {
    if (user && user?.user!.id) {
      setRecentViewIdStorage(fallback?.details.category_id);
      customerViewProduct({
        productId: fallback?.details.id,
        customerId: user?.user.id,
      });
    }
  }

  // get UpsellsProduct
  const {
    data: dataUpsellsProducts,
    isLoading: isLoadingUpsellsProduct,
    error: errorUpsellsProduct,
  } = useUpsellsProducts({
    categoryId: ROOT_CATEGORY_ID,
    neqProductId: dataProductKeyExchange?.id,
    productDisplayConfig: {
      currentPage: randomPageInRange(1, 120),
      pageSize: UPSELL_PAGE_SIZE,
      sortBy: "position",
    }, // TODO: update static to dynamic values
  });

  return (
    <SWRConfig value={{ fallback }}>
      {/* SEO */}
      {fallback?.metatags && (
        <NextSeo
          title={fallback?.metatags.title}
          description={fallback?.metatags.description}
          additionalMetaTags={keywords}
          noindex={noIndex}
          nofollow={noFollow}
          openGraph={{
            url: `${SEO_SITEURL}/products/${productKey}`,
            title: fallback?.metatags.title,
            description: fallback?.metatags.description,
            images: [
              {
                url: fallback?.metatags.image,
              },
            ],
          }}
          canonical={`${SEO_SITEURL}/products/${productKey}`}
        />
      )}

      {/* Main */}
      <section className="mainContent d-none d-lg-block">
        <div className="container-fluid">
          <div className="row">
            <div className="col mt-3 mt-lg-4">
              <Breadcrumb breadcrumbItems={breadcrums} />
            </div>
          </div>
        </div>
      </section>
      <section className="product-section mt-2 mt-lg-0">
        <div className="container-fluid">
          <div className="product-info rounded-2">
            {isLoadingProductDetail || errorProductDetail ? (
              <SkeletonProductInfo />
            ) : (
              dataProductDetail && (
                <ProductInfo
                  productKey={productKey}
                  productDetail={dataProductDetail!}
                />
              )
            )}
            {/* {fallback && fallback?.details ? (
              <ProductInfo
                productKey={productKey}
                productDetail={fallback?.details}
              />
            ) : (
              ""
            )} */}
          </div>

          <div className="product-detail">
            <div className="row">
              <div className="col-12 col-lg-9 detail">
                {/* {isLoadingProductDetail || errorProductDetail ? (
                  <Loader />
                ) : (
                  dataRelatedProducts?.items && (
                    <>
                      {dataProductDetail != undefined &&
                      dataProductDetail.additional_information != undefined ? (
                        <ProductSpecTable
                          productspecifics={
                            dataProductDetail!.additional_information
                          }
                        />
                      ) : (
                        ""
                      )}

                      <ProductSpecSheet
                        reviews={MockupProductReviews}
                        productDescHtml={dataProductDetail?.description}
                      />

                      <ProductCustomerReview
                        dataReviewProduct={dataReviewProducts?.items}
                        isLoadingReviewProducts={isLoadingReviewProducts}
                        productDetail={dataProductDetail!}
                        reviewCurrentPage={reviewCurrentPage}
                        dataReviewSummary={
                          dataReviewSummary?.[0]?.ratings || []
                        }
                        totalReviews={totalReviews}
                        totalReviewsFilter={totalReviewsFilter}
                        totalImageCount={totalImageCount}
                        setReviewCurrentPage={setReviewCurrentPage}
                        setFilteredReviewsRate={setFilteredReviewsRate}
                        setFilteredReviewsImg={setFilteredReviewsImg}
                      />
                    </>
                  )
                )} */}

                <>
                  {fallback &&
                  fallback?.details &&
                  fallback?.details.additional_information != undefined ? (
                    <>
                      <ProductSpecTable
                        productspecifics={
                          fallback?.details.additional_information
                        }
                      />
                      <ProductSpecSheet
                        reviews={MockupProductReviews}
                        productDescHtml={fallback?.details.description}
                      />
                    </>
                  ) : (
                    ""
                  )}

                  {/* Reviews */}
                  {isLoadingProductDetail || errorProductDetail ? (
                    <SkeletonProductCart />
                  ) : (
                    <ProductCustomerReview
                      dataReviewProduct={dataReviewProducts?.items}
                      isLoadingReviewProducts={isLoadingReviewProducts}
                      productDetail={dataProductDetail!}
                      reviewCurrentPage={reviewCurrentPage}
                      dataReviewSummary={dataReviewSummary?.[0]?.ratings || []}
                      totalReviews={totalReviews}
                      totalReviewsFilter={totalReviewsFilter}
                      totalImageCount={totalImageCount}
                      setReviewCurrentPage={setReviewCurrentPage}
                      setFilteredReviewsRate={setFilteredReviewsRate}
                      setFilteredReviewsImg={setFilteredReviewsImg}
                    />
                  )}
                </>
              </div>

              {/* Upsell Products */}
              {/* {isLoadingUpsellsProduct ? (
                <Loader />
              ) : (
                dataUpsellsProducts?.items && (
                  <UpsellsProducts
                    dataUpsellsProducts={dataUpsellsProducts?.items}
                  />
                )
              )} */}
              {fallback?.upsells && fallback?.upsells.data.items.length > 0 && (
                <UpsellsProducts
                  dataUpsellsProducts={fallback?.upsells.data.items}
                />
              )}
            </div>
          </div>

          {/* Related Products */}
          {/* {isLoadingRelatedProduct ? (
            <Loader />
          ) : (
            dataRelatedProducts?.items && (
              <RelatedProducts
                dataRelatedProduct={dataRelatedProducts?.items}
              />
            )
          )} */}
          {/* {fallback?.relateds && fallback?.relateds.data.items.length > 0 && (
            <RelatedProducts
              dataRelatedProduct={fallback?.relateds.data.items}
            />
          )} */}

          {/* Recent Views Product */}
          {dataProductKeyExchange !== undefined && !!user ? (
            <RecentView
              neqPid={dataProductKeyExchange?.id}
              cusId={user.user && String(user?.user.id)}
            />
          ) : (
            ""
          )}
        </div>
      </section>
    </SWRConfig>
  );
};

export default ProductDetailPage;

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const adminToken = await serverSideAdminToken();
    const { data: urlKey } = await serverSideUrlKeyExchange(
      String(params?.pkey)
    );
    const productId = Number(urlKey.id);

    // METADATA
    const { data: metaDatas } = await serverSideMetadataProductDetail(
      productId
    );

    if (adminToken && productId > 0) {
      const details = await serverSideProductDetail(
        String(productId),
        adminToken
      );
      const upsells = await serverSideUpSellProducts({
        categoryId: ROOT_CATEGORY_ID,
        neqProductId: String(params?.id),
        productDisplayConfig: {
          currentPage: randomPageInRange(1, 120),
          pageSize: UPSELL_PAGE_SIZE,
          sortBy: "position",
        },
        adminToken: adminToken,
      });
      const relateds = await serverSideRelatedProducts({
        categoryId: ROOT_CATEGORY_ID,
        neqProductId: String(params?.id),
        productDisplayConfig: {
          currentPage: randomPageInRange(1, 120),
          pageSize: RELATED_PAGE_SIZE,
          sortBy: "position",
        },
        adminToken: adminToken,
      });

      return {
        props: {
          fallback: {
            details: details,
            upsells: upsells,
            relateds: relateds,
            metatags: metaDatas,
          },
        },
        revalidate: 30,
      };
    } else {
      return {
        props: {
          fallback: null,
        },
        revalidate: 30,
      };
    }
  } catch {
    return {
      props: {
        fallback: null,
      },
      revalidate: 30,
    };
  }
};
